/**
 * PENDING 상태 주문 관리
 * 기존 order.ts에서 분리
 */

import { supabase } from '@/lib/supabase';
import { cartService } from '../cart';
import { errorLogger } from '@/lib/errorLogger';
import type { Order } from '@/types';
import { CreateOrderRequest } from './order-types';
import { validateStockAvailability, getCommissionRate, isValidDeadlineDiscountTime } from './order-validation';
import { cancelOrderInternal, sendOrderNotification } from './order-status';

/**
 * 장바구니 ID로 PENDING 주문 조회
 */
export async function getPendingOrderByCartId(
  cartId: string
): Promise<Order | null> {
  try {
    const { data, error } = await supabase
      .from('orders')
      .select(`
        *,
        items:order_items(
          *,
          product:products!product_id(*),
          offer:product_offers!offer_id(*)
        ),
        restaurant:restaurants(*),
        payment:payments(*)
      `)
      .eq('cart_id', cartId)
      .eq('status', 'PENDING')
      .eq('payment_status', 'PENDING')
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) {
      console.error('Error fetching pending order by cart:', error);
      return null;
    }

    return data;
  } catch (error) {
    console.error('Error in getPendingOrderByCartId:', error);
    return null;
  }
}

/**
 * PENDING 주문 취소
 */
export async function cancelPendingOrder(
  orderId: string,
  reason: string = '결제 페이지 이탈'
): Promise<{ success: boolean; error?: string }> {
  try {
    // PENDING 상태일 때만 취소 가능
    const { data: order, error: fetchError } = await supabase
      .from('orders')
      .select('status, payment_status')
      .eq('id', orderId)
      .single();

    if (fetchError || !order) {
      return { success: false, error: '주문을 찾을 수 없습니다' };
    }

    // 이미 결제 진행 중이거나 완료된 경우 취소 불가
    if (order.status !== 'PENDING' || order.payment_status !== 'PENDING') {
      console.log('Order is not in PENDING state, skipping cancellation');
      return { success: true }; // 이미 처리된 주문
    }

    // 주문 상태를 CANCELLED로 변경
    const { error: updateError } = await supabase
      .from('orders')
      .update({ 
        status: 'CANCELLED',
        payment_status: 'CANCELLED',
        cancelled_at: new Date().toISOString(),
        cancellation_reason: reason,
        updated_at: new Date().toISOString()
      })
      .eq('id', orderId)
      .eq('status', 'PENDING') // 동시성 제어: PENDING 상태일 때만 업데이트
      .eq('payment_status', 'PENDING');

    if (updateError) {
      console.error('Error cancelling pending order:', updateError);
      return { success: false, error: '주문 취소에 실패했습니다' };
    }

    // order_status_logs는 데이터베이스 트리거가 자동으로 처리함
    console.log(`Pending order ${orderId} cancelled: ${reason}`);
    return { success: true };
  } catch (error) {
    console.error('Error in cancelPendingOrder:', error);
    return { success: false, error: '주문 취소 중 오류가 발생했습니다' };
  }
}

/**
 * 장바구니에서 PENDING 주문 생성
 */
export async function createPendingOrderFromCart(
  userId: string,
  request: CreateOrderRequest
): Promise<{ success: boolean; error?: string; order?: Order }> {
  try {
    // Get cart with items
    const cart = await cartService.getCurrentCart(userId);
    
    if (!cart || !cart.items || cart.items.length === 0) {
      return { success: false, error: '장바구니가 비어있습니다.' };
    }

    // Validate stock availability for all items (검증만, 차감하지 않음)
    for (const item of cart.items) {
      const stockCheck = await validateStockAvailability(
        item.product_id, 
        item.quantity
      );
      if (!stockCheck.available) {
        return { 
          success: false, 
          error: `${item.product?.name}의 재고가 부족합니다. (남은 재고: ${stockCheck.currentStock})` 
        };
      }
    }

    // Calculate total and commission
    const totalAmount = cart.items.reduce(
      (sum, item) => sum + (item.quantity * item.unit_price), 
      0
    );

    // Determine order type from first item's offer
    const firstOffer = cart.items[0].offer;
    if (!firstOffer) {
      return { success: false, error: '주문 타입을 확인할 수 없습니다.' };
    }

    const orderType = firstOffer.offer_type;

    // 매장 마감할인 토글 off 시 DEADLINE_DISCOUNT 주문 차단
    if (orderType === 'DEADLINE_DISCOUNT') {
      const { data: restaurantConfig, error: restaurantError } = await supabase
        .from('restaurants')
        .select('deadline_discount_enabled')
        .eq('id', cart.restaurant_id)
        .single();

      if (restaurantError) {
        console.error('Failed to fetch restaurant deadline flag:', restaurantError);
        return { success: false, error: '매장 정보를 확인할 수 없습니다.' };
      }

      if (restaurantConfig?.deadline_discount_enabled === false) {
        return { success: false, error: '해당 매장은 마감할인 주문을 일시 중단했습니다.' };
      }
    }
    const commissionRate = getCommissionRate(orderType);
    const commissionAmount = Math.floor(totalAmount * commissionRate);

    // Validate pickup time for deadline discount
    if (orderType === 'DEADLINE_DISCOUNT' && !isValidDeadlineDiscountTime()) {
      return { success: false, error: '마감할인 주문 가능 시간이 아닙니다.' };
    }

    // 판매자 타임아웃 시간 설정
    // - 예약할인: 8시간
    // - 마감할인, 일반주문: 30분
    const timeoutDuration = orderType === 'RESERVATION_DISCOUNT' 
      ? 8 * 60 * 60 * 1000  // 8시간
      : 30 * 60 * 1000;      // 30분
    
    const sellerTimeoutAt = new Date(Date.now() + timeoutDuration).toISOString();

    // Create PENDING order (결제 대기 상태)
    const { data: orderData, error: orderError } = await supabase
      .from('orders')
      .insert([{
        buyer_id: userId,
        restaurant_id: cart.restaurant_id,
        order_type: orderType,
        total_amount: totalAmount,
        commission_amount: commissionAmount,
        pickup_time: request.pickup_time,
        special_requests: request.special_requests,
        payment_status: 'PENDING', // 결제 대기
        payment_method: request.payment_method,
        status: 'PENDING', // 주문 대기
        seller_timeout_at: sellerTimeoutAt, // 판매자 타임아웃 시간 추가
        cart_id: cart.id // 장바구니 ID 저장 (결제 성공 후 정리용)
      }])
      .select()
      .single();

    if (orderError) {
      errorLogger.logOrderError(
        orderError,
        'create_pending_order',
        undefined, // orderId not available yet
        userId,
        cart.restaurant_id
      );
      
      return { 
        success: false, 
        error: errorLogger.getUserFriendlyMessage(orderError, '주문 생성에 실패했습니다.')
      };
    }

    // Create order items (결제 전이므로 임시 저장)
    const orderItems = cart.items.map(item => ({
      order_id: orderData.id,
      product_id: item.product_id,
      offer_id: item.offer_id,
      quantity: item.quantity,
      unit_price: item.unit_price,
      total_price: item.quantity * item.unit_price,
      original_unit_price: item.product?.original_price ?? item.unit_price
    }));

    const { error: itemsError } = await supabase
      .from('order_items')
      .insert(orderItems);

    if (itemsError) {
      errorLogger.logSupabaseError(
        itemsError,
        'create_order_items',
        'order_items',
        {
          orderId: orderData.id,
          userId: userId,
          itemCount: orderItems.length,
          orderItems: orderItems
        }
      );

      // Rollback order
      await supabase.from('orders').delete().eq('id', orderData.id);
      return { 
        success: false, 
        error: errorLogger.getUserFriendlyMessage(itemsError, '주문 아이템 생성에 실패했습니다.')
      };
    }

    // Create payment record (pending 상태)
    const { data: paymentData } = await supabase
      .from('payments')
      .insert({
        order_id: orderData.id,
        amount: totalAmount,
        payment_method: request.payment_method,
        payment_gateway: 'TOSSPAYMENTS',
        status: 'pending'
      })
      .select()
      .single();

    return { 
      success: true, 
      order: { 
        ...orderData, 
        items: orderItems,
        payment: paymentData
      } 
    };
  } catch (error) {
    console.error('Error creating pending order:', error);
    return { success: false, error: '주문 생성 중 오류가 발생했습니다.' };
  }
}

/**
 * 주문 확정 (결제 완료 후)
 */
export async function confirmOrder(
  orderId: string,
  paymentData: {
    payment_key: string;
    transaction_id: string;
    paid_amount: number;
  }
): Promise<{ success: boolean; error?: string }> {
  try {
    // Get order and validate
    const { data: order } = await supabase
      .from('orders')
      .select('*')
      .eq('id', orderId)
      .single();
      
    if (!order) {
      return { success: false, error: '주문을 찾을 수 없습니다.' };
    }

    if (order.payment_status !== 'PENDING') {
      return { success: false, error: '이미 처리된 주문입니다.' };
    }

    // Get cart ID and order type from order
    const cartId = order.cart_id;
    const orderType = order.order_type;
    
    if (!cartId) {
      return { success: false, error: '장바구니 정보를 찾을 수 없습니다.' };
    }

    // Final stock validation and consumption (예약할인은 재고 차감하지 않음)
    const stockResult = await cartService.consumeCartStock(cartId, orderType);
    if (!stockResult.success) {
      // Cancel the order if stock consumption fails
      await cancelOrderInternal(orderId, '재고 부족으로 인한 자동 취소');
      return { 
        success: false, 
        error: stockResult.error || '재고 차감에 실패했습니다.' 
      };
    }

    // Update payment record
    const { error: paymentError } = await supabase
      .from('payments')
      .update({
        status: 'completed',
        payment_key: paymentData.payment_key,
        transaction_id: paymentData.transaction_id,
        paid_at: new Date().toISOString(),
        amount: paymentData.paid_amount
      })
      .eq('order_id', orderId);

    if (paymentError) {
      // Restore stock if payment update fails (예약할인은 제외)
      await cartService.restoreCartStock(cartId, orderType);
      return { success: false, error: '결제 정보 업데이트에 실패했습니다.' };
    }

    // Update order status
    const { error: orderError } = await supabase
      .from('orders')
      .update({ 
        payment_status: 'PAID',
        status: 'ACCEPTED', // 결제 완료 후 자동 수락
        updated_at: new Date().toISOString()
      })
      .eq('id', orderId);

    if (orderError) {
      // Restore stock if order update fails (예약할인은 제외)
      await cartService.restoreCartStock(cartId, orderType);
      return { success: false, error: '주문 상태 업데이트에 실패했습니다.' };
    }

    // Clear cart after successful payment confirmation
    await cartService.clearCart(cartId);

    // Send order accepted notification
    await sendOrderNotification(orderId, 'ACCEPTED');

    return { success: true };
  } catch (error) {
    console.error('Error confirming order:', error);
    return { success: false, error: '주문 확정 중 오류가 발생했습니다.' };
  }
}
